import { createApi, BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { Api } from "laravel-request";

/** Minimal type for laravel-request query - library doesn't export types */
export type LaravelQueryBuilder = Record<string, unknown> & {
    with: (...args: unknown[]) => LaravelQueryBuilder;
    where: (key: string, value: unknown) => LaravelQueryBuilder;
    first: (
        onSuccess?: (r: unknown) => void,
        onError?: (e: unknown) => void
    ) => LaravelQueryBuilder;
    all: (onSuccess?: (r: unknown) => void, onError?: (e: unknown) => void) => void;
    call: (onSuccess?: (r: unknown) => void, onError?: (e: unknown) => void) => void;
};

export interface LaravelRequestQuery {
    resource: string;
    action: string;
    method?: "GET" | "POST" | "PUT" | "DELETE";
    params?: Record<string, unknown>;
    data?: Record<string, unknown>;
    id?: string | number;
    queryBuilder?: (query: LaravelQueryBuilder) => LaravelQueryBuilder | void;
    useAll?: boolean; // Флаг для использования .all() вместо .call()
}

export interface LaravelRequestError {
    status: number;
    data: unknown;
}

/**
 * Кастомный baseQuery для работы с laravel-request
 * @param resource
 * @param action
 * @param method
 * @param params
 * @param data
 * @param id
 * @param queryBuilder
 * @param useAll
 */
const laravelRequestBaseQuery: BaseQueryFn<
    LaravelRequestQuery,
    unknown,
    LaravelRequestError
> = async ({ resource, action, method = "GET", params, data, id, queryBuilder, useAll }) => {
    try {
        return await new Promise((resolve, reject) => {
            let query: LaravelQueryBuilder;

            if (method === "GET") {
                query = Api.get(resource, action, params) as unknown as LaravelQueryBuilder;
            } else if (method === "POST") {
                query = Api.post(
                    resource,
                    action,
                    data || params
                ) as unknown as LaravelQueryBuilder;
            } else if (method === "PUT" && id) {
                query = Api.putArg(
                    resource,
                    action,
                    id,
                    data || params
                ) as unknown as LaravelQueryBuilder;
            } else if (method === "DELETE" && id) {
                query = Api.delete(resource, action, id, data) as unknown as LaravelQueryBuilder;
            } else {
                reject({
                    error: {
                        status: 400,
                        data: "Invalid method or missing id",
                    },
                });
                return;
            }

            if (queryBuilder) {
                query = queryBuilder(query) ?? query;
            }

            if (useAll && typeof query.all === "function") {
                query.all(
                    (response: unknown) => {
                        const r = response as { data?: unknown };
                        const data = r?.data !== undefined ? r.data : response;
                        resolve({ data });
                    },
                    (error: unknown) => {
                        const err = error as {
                            status?: number;
                            xhr?: { status?: number };
                            responseData?: unknown;
                            meta?: unknown;
                        };
                        reject({
                            error: {
                                status: err?.status || err?.xhr?.status || 500,
                                data: err?.responseData || err?.meta || error || "Unknown error",
                            },
                        });
                    }
                );
            } else if (
                typeof query.first === "function" &&
                queryBuilder &&
                query.toString().includes("first")
            ) {
                // Для одного элемента используем .first() (если queryBuilder вернул query с .first())
                query.first(
                    (response: unknown) => {
                        const r = response as { data?: unknown };
                        const data = r?.data !== undefined ? r.data : response;
                        resolve({ data });
                    },
                    (error: unknown) => {
                        const err = error as {
                            status?: number;
                            xhr?: { status?: number };
                            responseData?: unknown;
                            meta?: unknown;
                        };
                        reject({
                            error: {
                                status: err?.status || err?.xhr?.status || 500,
                                data: err?.responseData || err?.meta || error || "Unknown error",
                            },
                        });
                    }
                );
            } else {
                // Fallback на .call()
                query.call(
                    (response: unknown) => {
                        const r = response as { data?: unknown };
                        const data = r?.data !== undefined ? r.data : response;
                        resolve({ data });
                    },
                    (error: unknown) => {
                        const err = error as {
                            status?: number;
                            xhr?: { status?: number };
                            responseData?: unknown;
                            meta?: unknown;
                        };
                        reject({
                            error: {
                                status: err?.status || err?.xhr?.status || 500,
                                data: err?.responseData || err?.meta || error || "Unknown error",
                            },
                        });
                    }
                );
            }
        });
    } catch (err: unknown) {
        const error = err as { error?: { status?: number; data?: unknown } };
        return {
            error: {
                status: error?.error?.status || 500,
                data: error?.error?.data || err || "Unknown error",
            },
        };
    }
};

/**
 * Теги для инвалидации кеша
 */
export const apiTags = {
    Application: "Application",
    Contact: "Contact",
    Client: "Client",
    Task: "Task",
    Service: "Service",
    Portfolio: "Portfolio",
    File: "File",
    Category: "Category",
    Hierarchy: "Hierarchy",
    Permission: "Permission",
    Settings: "Settings",
    Employee: "Employee",
} as const;

export type ApiTag = keyof typeof apiTags;

const apiTagValues: ApiTag[] = Object.keys(apiTags) as ApiTag[];

export const baseApi = createApi({
    reducerPath: "api",
    baseQuery: laravelRequestBaseQuery,
    tagTypes: apiTagValues,
    keepUnusedDataFor: 600,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: true,
    endpoints: () => ({}),
});
